import React, { useState, useEffect } from 'react';
import { Checkbox, Spinner } from 'ui-component-library';
import { useSelector, useDispatch } from 'react-redux';
import { string, bool, func } from 'prop-types';

import {
  MainContainer,
  TextLabel,
  TextContainer,
  SubmitButton,
  TextTitle,
  SpinnerContainer,
} from './office-preferences.style';
import theme from '../../../../theme';
import { postUserProfileSettings } from '../../../../utils/api/metadata';
import { getUserProfileSettings } from '../../../../store/profile-settings/thunkActions';
import { showErrorMessage, showSuccessMessage, logger } from '../../../../utils/common';
import { formatErrorResponse } from '../../../../utils/mappers';
/**
 * Office Preferences Form Component
 * @param {object} props - Object containing the props for this component
 * @param {string} props.description - description text
 * @param {string} props.title - text title
 * @param {bool} props.isModal - if the office preferences is displayed inside a modal
 * @param {func} props.closeModal - close the model function
 * @return {element}
 */
const OfficePreferences = ({
  description,
  title,
  isModal,
  closeModal,
}) => {
  const profileSettings = useSelector((state) => state.profileSettings);
  const metadata = useSelector((state) => state.metadata);
  const dispatch = useDispatch();

  const [selectedOffices, setSelectedOffices] = useState(null);
  const [loading, setLoading] = useState(false);

  // creates an object where keys are the offices and value is boolean for if the office is selected or not
  useEffect(() => {
    if (!selectedOffices && profileSettings?.data && metadata?.data?.offices) {
      const initialSelectedOffices = metadata.data.offices.reduce((acc, office) => (
        { ...acc, [office]: profileSettings.data.officeNames?.includes(office) }
      ), {});
      let sortedOffices = {};
      Object.keys(initialSelectedOffices).sort().forEach((office) => {
        if (office !== 'Other (Other)') {
          sortedOffices = {
            ...sortedOffices,
            [office]: initialSelectedOffices[office],
          };
        }
      });
      sortedOffices = {
        ...sortedOffices,
        'Other (Other)': initialSelectedOffices['Other (Other)'],
      };
      setSelectedOffices(sortedOffices);
    }
  }, [profileSettings, metadata, selectedOffices]);

  /**
   * Handles form submit to update a user's office settings
   */
  const handleSubmit = () => {
    setLoading(true);
    // update user profile settings with selected offices
    postUserProfileSettings({
      officeNames: Object.keys(selectedOffices).filter((office) => selectedOffices[office] === true),
    }).then(() => {
      dispatch(getUserProfileSettings());
      showSuccessMessage('Your user profile settings have been updated.');
    }).catch((error) => {
      logger.error(formatErrorResponse(error));
      showErrorMessage('Something went wrong. The user profile preferences service is currently unavailable. Please try again later.');
    }).finally(() => {
      closeModal();
      setLoading(false);
    });
  };

  if (loading) {
    return (
      <SpinnerContainer>
        <Spinner />
      </SpinnerContainer>
    );
  }

  return (
    <MainContainer isModal={isModal}>
      <TextContainer>
        {!isModal && <TextLabel>{title}</TextLabel>}
        {isModal && <TextTitle>{title}</TextTitle>}
        {description}
      </TextContainer>
      {selectedOffices && Object.keys(selectedOffices).map((office) => (
        <Checkbox
          key={office}
          checked={selectedOffices[office]}
          onValueChange={() => { setSelectedOffices({ ...selectedOffices, [office]: !selectedOffices[office] }); }}
          name={office}
          text={office}
          labelColor={theme.palette.black[100]}
        />
      ))}
      <SubmitButton
        isModal={isModal}
        onClick={() => handleSubmit()}
        text="Save"
        isDisabled={selectedOffices ? Object.keys(selectedOffices).every((office) => !selectedOffices[office]) : false}
      />
    </MainContainer>
  );
};

OfficePreferences.propTypes = {
  description: string,
  title: string,
  isModal: bool,
  closeModal: func,
};

OfficePreferences.defaultProps = {
  description: '',
  title: '',
  isModal: false,
  closeModal: () => {},
};

export default OfficePreferences;
